import Button, { ButtonProps } from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { SxProps, Theme } from "@mui/material/styles";
import React, { ReactNode, useState, useEffect, useRef } from "react";

/** Called by an export as it goes, e.g. (pagesFetched, totalPages). */
export type ExportProgressReporter = (done: number, total: number) => void;

export interface ICustomButtonProps extends Omit<ButtonProps, "onClick"> {
  label?: ReactNode;
  icon?: React.ReactElement;
  iconPosition?: "left" | "center" | "right";
  loading?: boolean;
  className?: string;
  fullWidth?: boolean;
  iconButtonSize?: number;
  sx?: SxProps<Theme>;
  borderRadius?: string | number;
  textColor?: string;
  borderColor?: string;
  bgColor?: string;
  hoverBgColor?: string;
  isIconOnly?: boolean;
  isTextOnly?: boolean;
  disableElevation?: boolean;
  minWidth?: number;
  minHeight?: number;
  bold?: string;
  onClick?: (reportProgress: ExportProgressReporter) => void | Promise<void>;
  disabled?: boolean;
  /**
   * When onClick returns a promise, show "Exporting… N%" until it settles.
   * The export calls reportProgress(done, total) as it goes; until it does,
   * the bar runs without a number.
   */
  showExportProgress?: boolean;
  useRealProgressTracking?: boolean;
  exportId?: string;
  onExportProgress?: (progress: {
    progress: number;
    message: string;
    status: string;
  }) => void;
}

const CustomButton: React.FC<
  ICustomButtonProps & { children?: React.ReactNode }
> = ({
  loading = false,
  iconPosition = "left",
  iconButtonSize = 40,
  label,
  startIcon,
  endIcon,
  textColor,
  borderColor = "transparent",
  bgColor,
  borderRadius,
  minWidth,
  minHeight,
  bold = false,
  size = "medium",
  sx,
  isIconOnly = !label && (startIcon || endIcon),
  isTextOnly = label && !startIcon && !endIcon,
  disabled = false,
  children,
  onClick,
  hoverBgColor,
  showExportProgress = false,
  useRealProgressTracking = false,
  exportId,
  onExportProgress,
  ...props
}) => {
  const [internalLoading, setInternalLoading] = useState(false);
  // null until the export reports something; then 0-100.
  const [exportProgress, setExportProgress] = useState<number | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [progressMessage, setProgressMessage] = useState("");
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (resetTimer.current) clearTimeout(resetTimer.current);
    };
  }, []);

  const finishExport = (message: string, delay: number) => {
    if (!mounted.current) return;
    setProgressMessage(message);
    resetTimer.current = setTimeout(() => {
      if (!mounted.current) return;
      setIsExporting(false);
      setExportProgress(null);
      setProgressMessage("");
    }, delay);
  };

  const reportProgress: ExportProgressReporter = (done, total) => {
    if (!mounted.current || !(total > 0)) return;
    // 100% is kept for when the file is actually ready.
    const percent = Math.min(99, Math.max(0, Math.floor((done / total) * 100)));
    setExportProgress((current) =>
      current === null || percent > current ? percent : current
    );
  };

  const handleClick = async () => {
    if (!onClick || isExporting) return;
    if (!showExportProgress) {
      // Unchanged for every other button: no argument, no loading state.
      await (onClick as () => void | Promise<void>)();
      return;
    }
    const result = onClick(reportProgress);
    if (!(result instanceof Promise)) return;

    if (resetTimer.current) clearTimeout(resetTimer.current);
    setIsExporting(true);
    setExportProgress(null);
    setProgressMessage("");
    try {
      await result;
      if (mounted.current) setExportProgress(100);
      finishExport("Exported!", 800);
    } catch (error) {
      // The page reports what went wrong; re-throwing from a click handler
      // would only end up as an uncaught error.
      console.error("Export failed:", error);
      finishExport("Export failed", 1500);
    }
  };

  const isDisabled = disabled || loading || internalLoading || isExporting;

  const showProgress = isExporting;

  const buttonSx: SxProps<Theme> = {
    textTransform: "none",
    minWidth:
      minWidth ??
      (isIconOnly
        ? iconButtonSize
        : isTextOnly && size === "small"
        ? 30
        : size === "large"
        ? 248.41
        : size === "medium"
        ? 110
        : undefined),
    minHeight:
      minHeight ??
      (isIconOnly
        ? iconButtonSize
        : isTextOnly && size === "small"
        ? 30
        : isTextOnly
        ? 36
        : undefined),
    padding: isIconOnly
      ? 0
      : isTextOnly && size === "small"
      ? "4px 8px"
      : isTextOnly
      ? "6px 12px"
      : undefined,
    justifyContent:
      isIconOnly || iconPosition === "center"
        ? "center"
        : isTextOnly
        ? "center"
        : undefined,
    display: "flex",
    alignItems: "center",
    flexDirection: showProgress ? "column" : "row",
    color: textColor,
    borderColor: borderColor,
    backgroundColor: bgColor,
    borderRadius: borderRadius,
    fontWeight: bold ? "bold" : undefined,
    cursor: isDisabled ? "not-allowed" : undefined,
    opacity: isDisabled ? 0.6 : 1,
    position: "relative",
    overflow: "hidden",
    ":hover": {
      backgroundColor: hoverBgColor,
    },
    ...sx,
  };

  const renderButtonContent = () => {
    if (showProgress) {
      return (
        <Box sx={{ width: "100%", textAlign: "center" }}>
          <Typography variant="caption" sx={{ mb: 0.5, fontSize: "0.75rem" }}>
            {progressMessage ||
              (exportProgress === null
                ? "Exporting…"
                : `Exporting… ${exportProgress}%`)}
          </Typography>
          <LinearProgress
            variant={exportProgress === null ? "indeterminate" : "determinate"}
            value={exportProgress ?? 0}
            sx={{
              width: "100%",
              height: 4,
              borderRadius: 2,
              backgroundColor: "rgba(255, 255, 255, 0.3)",
              "& .MuiLinearProgress-bar": {
                backgroundColor: textColor || "#fff",
                borderRadius: 2,
              },
            }}
          />
        </Box>
      );
    }

    if (loading || internalLoading) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress
            size={16}
            sx={{ color: textColor || "currentColor" }}
          />
          {label}
        </Box>
      );
    }

    if (children) return children;
    if (isIconOnly) return startIcon || endIcon;
    return label;
  };

  return (
    <Button
      {...props}
      sx={buttonSx}
      startIcon={
        !isIconOnly && !isTextOnly && iconPosition === "left" && !showProgress
          ? startIcon
          : undefined
      }
      endIcon={
        !isIconOnly && !isTextOnly && iconPosition === "right" && !showProgress
          ? endIcon
          : undefined
      }
      disabled={isDisabled}
      onClick={handleClick}
    >
      {renderButtonContent()}
    </Button>
  );
};

export default CustomButton;
